import { icons } from 'common'
import { ContentSwitcherByState } from 'components/Atoms/ContentSwitcherByState'
import { ASSETS, getAssetInfo } from 'constants/assets'
import { LONG_DASH } from 'constants/index'
import { useActivePnl } from 'hooks/games/lvg/useActivePnL'
import { useMemo } from 'react'
import { currencyFormatter } from 'utils'
import { isEmpty } from 'lodash'
import { useYoloModal } from 'lib/yoloModals/useYoloModal'
import { isStockMarketOpen } from 'utils'
import { useLvgState } from 'hooks/games/lvg/useLvgState'
import { LvgTwitterShareBid } from 'components/Molecules/LvgTwitterSharedBid.button'
import { SingleDataLoader } from 'components/Atoms/SingleDataLoader'
import { useDebouncedCallback } from 'utils/hooks/useDebounceCallback'
import { LVG_ORDER_STATE } from 'constants/games/lvg/lvgOrderState'
import { SingleContentToggle } from 'components/Atoms/SingleContentToggle'
import { useLvgOrderManager } from 'hooks/games/lvg/useYourBidsOrders'

  const getAssetDecimals = (symbol) => {
    return ASSETS.find((asset) => asset.id === symbol)?.fiatDecimals
  }

export const YourBidsRow = ({ order }) => {
  const { updateModal } = useYoloModal()
  const { setActiveOrder } = useLvgState()
  const { closeOrder } = useLvgOrderManager()

  const showOrderModal = (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (isEmpty(order)) return
    const orderModalObj = {
      show: true,
      id: 'order',
      props: { order },
      backdropBlurred: false,
    }
    updateModal(orderModalObj)
  }

  const showOrderInGraph = (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (isEmpty(order)) return
    const assetInfo = getAssetInfo(order.asset)
    if (assetInfo.hasTime && !isStockMarketOpen().state) return
    setActiveOrder(order)
  }

  const editTPSL = (e) => {
    e?.preventDefault()
    e?.stopPropagation()

    if (isEmpty(order)) return
    const updateOrderModalObj = {
      show: true,
      id: 'updateOrder',
      props: { order },
      backdropBlurred: false,
    }
    updateModal(updateOrderModalObj)
  }
  const cashOut = useDebouncedCallback((e) => {
    closeOrder(order)
    // setClosingOrder(true)
  }, 500)

  const onCashOut = (e, isCashoutGrayout) => {
    e?.preventDefault()
    e?.stopPropagation()
    !isCashoutGrayout && cashOut(order)
  }

  const { activePnl } = useActivePnl(order)

  const isProfit = useMemo(() => activePnl >= 0, [activePnl])
  const isPending = useMemo(() => order.status === LVG_ORDER_STATE.PENDING, [order?.status])
  const isLive = useMemo(() => order.status === LVG_ORDER_STATE.LIVE, [order?.status])
  const isClosing = useMemo(() => order.status === LVG_ORDER_STATE.CLOSING, [order?.status])
  const isBusted = useMemo(() => order.status === LVG_ORDER_STATE.BUSTED, [order?.status])
  const isCloning = useMemo(() => !!order.parentOrderId, [order?.parentOrderId])
  const isCashoutGrayout = useMemo(() => {
    const assetInfo = getAssetInfo(order.asset)
    return assetInfo.hasTime && !isStockMarketOpen().state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.assetType])
  const roi = useMemo(
    () => (activePnl * 100) / Number(order.userFillDollarAmount),
    [activePnl, order?.userFillDollarAmount]
  )
  const assetInfo = useMemo(
    () =>
      ASSETS.filter((asset) => {
        return asset.id === order.asset
      }).at(0),
    [order?.asset]
  )

  if (!assetInfo) return null

  return (
    <div className='yourbids_table_row' onClick={showOrderInGraph}>
      {/* Status Cell */}
      <label
        onClick={showOrderModal}
        className={`status ${isPending ? 'pending' : ''} ${isPending || isLive || isClosing ? 'open' : 'closed'} ${
          isProfit ? 'profit' : 'loss'
        }`}
      >
        {(isPending || isLive || isClosing) && (
          <img alt='status' src={isProfit ? icons.open_bid_icon_green : icons.open_bid_icon_red} />
        )}
        {!(isPending || isLive || isClosing) && <img alt='status' src={icons.closed_order_icon} />}
        <span>{isPending ? 'Pending' : isLive || isClosing ? 'Open' : 'Closed'}</span>
        {!isPending && <div className='bid_information' />}
      </label>

      {/* Asset Cell */}
      <label
        className={`asset ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        <div className='asset_icon'>
          <img alt='coinIcon' src={assetInfo?.icon} />
          {/* <img alt='coinIcon' src={assetInfo?.icon} /> */}
        </div>
        {!!assetInfo.icon2 && (
          <div className='asset_icon'>
            <img alt='coinIcon' src={assetInfo?.icon2} />
          </div>
        )}
        <span>{assetInfo.uiName}</span>
      </label>

      {/* Entry Price Cell */}
      <label
        className={`entry ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        {order?.entryPrice
          ? currencyFormatter(order.entryPrice, {
              noCurrencySign: true,
              decimalDigits: assetInfo.fiatDecimals,
            })
          : LONG_DASH}
      </label>

      {/* Bid Cell */}
      <label
        className={`bidamount ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        <div className={`bid_direction ${order.side === 'buy' ? 'above' : 'below'}`}></div>
        <span>{currencyFormatter(order.amount)}</span>
      </label>

      {/* Upfront fees */}
      {/* <label
        className={`pandl  ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        <SingleContentToggle
          toggle={isPending || isClosing}
          trueContent={LONG_DASH}
          falseContent={currencyFormatter(order.upfrontFee, {})}
        />
      </label> */}

      {/* funding fees */}
      <label
        className={`pandl  ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        <SingleContentToggle
          toggle={isPending || isClosing}
          trueContent={LONG_DASH}
          falseContent={currencyFormatter(order.fundingFee, {})}
        />
      </label>

      {/* Bust Cell */}
      <label className={`bust ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}>
        {order?.bustPrice
          ? currencyFormatter(order.bustPrice, {
              noCurrencySign: true,
              decimalDigits: assetInfo.fiatDecimals,
            })
          : LONG_DASH}
      </label>

      {/* Multiplier Cell */}
      <label
        className={`multiplier ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        x{order.leverage}
      </label>

      {/* Exit Price Cell */}
      <label className={`exit ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}>
        <SingleContentToggle
          toggle={isBusted}
          trueContent={LONG_DASH}
          falseContent={
            order?.exitPrice
              ? currencyFormatter(order.exitPrice, {
                  noCurrencySign: true,
                  decimalDigits: assetInfo.fiatDecimals,
                })
              : LONG_DASH
          }
        />
      </label>

      {/* TPSL Cell */}
      <label
        className={`tpsl ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
        onClick={editTPSL}
      >
        <>
          TP: {order?.takeProfitPrice ? currencyFormatter(order?.takeProfitPrice, { decimalDigits: getAssetDecimals(order?.asset), useLongNumber: true }) : `${LONG_DASH}`}
          <br />
          SL: {order?.userStopLossPrice ? currencyFormatter(order?.userStopLossPrice, { decimalDigits: getAssetDecimals(order?.asset), useLongNumber: true }) : `${LONG_DASH}`}
          <br />
          TS: {order?.trailingStopPrice? currencyFormatter(order?.trailingStopPrice, { decimalDigits: getAssetDecimals(order?.asset), useLongNumber: true }): `${LONG_DASH}`}
          {/* {order?.trailingStopPercent ? `${currencyFormatter(order?.trailingStopPercent, { noCurrencySign: true })}%`
            : order?.trailingStopValue ? currencyFormatter(order?.trailingStopValue, { decimalDigits: getAssetDecimals(order?.asset), useLongNumber: true }) : `${LONG_DASH}`} */}
        </>
        {isLive && <button className='edit_tpsl'></button>}
      </label>

      {/* PnL Cell */}
      <label
        className={`pandl ${isProfit ? 'up' : 'down'} ${isPending || isLive || isClosing ? 'open' : 'closed'} ${
          isProfit ? 'profit' : 'loss'
        }`}
      >
        {currencyFormatter(activePnl, {
          decimalDigits: assetInfo.fiatDecimals,
          useLongNumber: true,
        })}
      </label>

      {/* ROI Cell */}
      <label
        className={`roi ${isProfit ? 'up' : 'down'} ${isPending || isLive || isClosing ? 'open' : 'closed'} ${
          isProfit ? 'profit' : 'loss'
        }`}
      >
        {currencyFormatter(roi, { noCurrencySign: true })}%
      </label>

      {/* Share Cell */}
      <label
        className={`share_bid ${isPending || isLive || isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}
      >
        <LvgTwitterShareBid variant='icon' order={order} />
      </label>

      {/* Action Cell */}
      <ContentSwitcherByState
        noWrapper
        activeState={order.status}
        stateObject={{
          [LVG_ORDER_STATE.PENDING]: (
            <label
              className={`action_status ${isPending || isLive ? 'open' : 'closed'} 
              ${isProfit ? 'profit' : 'loss'} pending`}
            >
              <SingleDataLoader loading={true} data='' />
              <span>Pending</span>
            </label>
          ),
          [LVG_ORDER_STATE.LIVE]: (
            <label
              className={`action_status ${isLive ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'} ${
                isCashoutGrayout ? 'grayout' : ''
              }`}
            >
              <div onClick={(e) => onCashOut(e, isCashoutGrayout)}>
                <button className='cashout'>Cash out</button>
                <button className='cashout_icon'></button>
              </div>
            </label>
          ),
          [LVG_ORDER_STATE.CLOSING]: (
            <label className={`action_status ${isClosing ? 'open' : 'closed'} ${isProfit ? 'profit' : 'loss'}`}>
              <div onClick={onCashOut}>
                <button className='cashout'>
                  <SingleDataLoader loading={isClosing} data={'Cash out'} />
                </button>
                <button className='cashout_icon'></button>
              </div>
            </label>
          ),
          [LVG_ORDER_STATE.CLOSED]: (
            <label className={`result cashed_out ${isProfit ? 'profit' : 'loss'}`}>
              <span>Cashed out</span>
            </label>
          ),
          [LVG_ORDER_STATE.FUNDING]: (
            <label className={`result funding ${isProfit ? 'profit' : 'loss'}`}>
              <span>funding</span>
            </label>
          ),
          [LVG_ORDER_STATE.BUSTED]: (
            <label className='result busted'>
              <span>Busted</span>
            </label>
          ),
        }}
      />

      {/* Cloning Cell */}
      <label
        className={`${isCloning && isLive ? 'bid_cloning_ind' : ''} ${
          isPending || isLive || isClosing ? 'open' : 'closed'
        } ${isProfit ? 'profit' : 'loss'}`}
      />
    </div>
  )
}
